import { useState, useEffect, useContext, useCallback, useMemo } from "react";
import AppBackService from "../services/appback";
import { createAppbackResourceAdapter } from "../liveTap/appbackResourceAdapter";
import useSymbolName from "./useSymbolName";
import { RootContext } from "../context/root-provider";
import { backgroundImageUrl } from "../services/utilities";
import { Box, Skeleton, DialogContentText, TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ErrorIcon from "@mui/icons-material/Error";

const imageStyles = {
  width: "200px",
  marginRight: "10px",
  marginBottom: "10px",
  boxSizing: "border-box",
  cursor: "pointer",
  position: "relative",
  "& img": {
    width: "200px",
    display: "block",
  },
  "&:hover": {
    opacity: 0.7,
  },
};
const styles = {
  thumbnail: {
    marginRight: "20px",
    marginBottom: "10px",
    marginTop: "10px",
    display: "inline-block",
  },
  imageSelect: {
    marginTop: "10px",
    display: "flex",
    flexWrap: "wrap",
  },
  image: imageStyles,
  selectedImage: {
    ...imageStyles,
    "& img": {
      width: "200px",
      display: "block",
      outline: "4px solid var(--color-accent-primary)",
    },
  },
  headingRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  heading: {
    fontWeight: "bold",
    color: "var(--color-text-primary)",
    whiteSpace: "nowrap",
    marginBottom: 0,
  },
  filter: {
    maxWidth: "240px",
    "& .MuiOutlinedInput-root": {
      paddingLeft: "10px",
    },
  },
  layoutTitle: {
    position: "absolute",
    textAlign: "center",
    top: "8px",
    fontWeight: "bold",
    padding: "4px 8px",
    borderRadius: "4px",
    left: "50%",
    color: "white",
    transform: "translateX(-50%)",
    textShadow: "1px 1px 2px black",
  },
};

const defaultLayout = "605edbebcee46a5c13d585c7";

export default function useSelectLayout() {
  const [error, setError] = useState(false);
  const [layouts, setLayouts] = useState();
  const [selectedLayout, setSelectedLayout] = useState(null);
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [filter, setFilter] = useState("");
  // A background holds its primary symbol by symbol_id; the search matches its
  // name, read once a search is typed (the picker is mounted on every page).
  const catalog = useMemo(() => createAppbackResourceAdapter(), []);
  const nameOf = useSymbolName(filter ? catalog : null);

  const { userInfo, loggingIn } = useContext(RootContext);

  // fetch data
  useEffect(() => {
    async function fetchData() {
      const data = await AppBackService.getLayouts();
      if (!data || data.error || !data.layouts?.length) {
        setError(true);
      } else {
        const layoutsObj = {};
        for (const layout of data.layouts) {
          if (layout.active) {
            const defaultBackground = layout.backgrounds.find((bg) => bg.default) || layout.backgrounds[0];
            layout.backgrounds.sort((a, b) => (defaultBackground === a.id ? -1 : defaultBackground === b.id ? 1 : 0));
            layoutsObj[layout.id] = layout;
          }
        }
        setLayouts(layoutsObj);
      }
    }
    if (userInfo?.username && !loggingIn) {
      fetchData();
    }
  }, [loggingIn, userInfo]);

  const resetSelection = useCallback(() => {
    setSelectedLayout(null);
    setSelectedBackground(null);
  }, []);

  const imagesSkeleton = useMemo(
    () =>
      Array.from(new Array(3)).map((_d, i) => (
        <Skeleton key={i} variant="rectangular" width={190} height={120} sx={styles.thumbnail} />
      )),
    []
  );

  const layoutSelector = useMemo(() => {
    return error ? (
      <>
        <ErrorIcon />
        <DialogContentText>Error loading layouts, please try again later.</DialogContentText>
      </>
    ) : (
      <>
        <Box sx={styles.headingRow}>
          <DialogContentText sx={styles.heading}>Select a Background:</DialogContentText>
          <TextField
            sx={styles.filter}
            placeholder="Search backgrounds..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: "var(--color-text-tertiary)" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {layouts ? (
          <Box sx={styles.imageSelect}>
            {Object.values(layouts)
              .sort((a, b) => {
                const layout = Object.values(layouts).find((layout) => layout.default)?.id || defaultLayout;
                return layout === a.id ? -1 : layout === b.id ? 1 : 0;
              })
              .filter((layout) => layout.backgrounds.length)
              .flatMap((layout) =>
                layout.backgrounds
                  .filter((bg) => {
                    // filter layout name, description, styles, and primary symbol
                    if (!filter) return true;

                    function bgMatches(source) {
                      return source?.toLowerCase().includes(filter.toLowerCase());
                    }

                    if (bgMatches(layout.name)) return true;
                    if (bgMatches(bg.description)) return true;
                    if (bg.styles?.some(bgMatches)) return true;
                    if (bgMatches(nameOf(bg.primarySymbolId))) return true;
                    return false;
                  })
                  .map((bg) => (
                    <Box
                      key={bg.id}
                      sx={selectedBackground === bg.id ? styles.selectedImage : styles.image}
                      onClick={() => {
                        setSelectedBackground(bg.id);
                        setSelectedLayout(layout.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          setSelectedBackground(bg.id);
                          setSelectedLayout(layout.id);
                        }
                      }}
                      tabIndex={0}
                      role="button">
                      <img src={backgroundImageUrl(bg.id)} alt={bg.description} draggable="false" />
                      {/* <Typography sx={styles.layoutTitle}>{toTitleCase(layout.name)}</Typography> */}
                    </Box>
                  ))
              )}
          </Box>
        ) : (
          imagesSkeleton
        )}
      </>
    );
  }, [error, filter, layouts, imagesSkeleton, nameOf, selectedBackground]);

  return { error, selectedLayout, selectedBackground, layoutSelector, resetSelection };
}
